/**
 * Free recruiter-grade CV review — pure half (prompt, input framing, parser).
 *
 * Owner 2026-10-29: "free unlimited claude review everybody cv resume like
 * best world class hr for job seeker". The paid Rp 12k report
 * (`cv-review-core.ts`) sold 0 times ever (seeker-outcome-phase0 F5, PR
 * #7477), so this is the free path for everyone, guests included, behind the
 * `free-cv-review` PostHog flag.
 *
 * The rubric is the one a strong recruiter applies in the first pass:
 *   ats_parse  — can an applicant tracking system read it at all,
 *   impact     — are achievements quantified, or only duties listed,
 *   keywords   — does it carry the words live jobs for this role ask for,
 *   seniority  — does it signal the level the seeker is aiming at,
 *   clarity    — can a human scan it in six seconds.
 *
 * Every finding must quote a line that really is in the CV and give the
 * rewritten line. {@link parseFreeCvReview} drops any finding whose quote
 * cannot be found in the CV, so a model that invents a line never reaches
 * the seeker, and it recomputes the overall score from the dimension scores
 * instead of trusting the model's arithmetic.
 *
 * No I/O here; the runner is `free-cv-review-core.ts`.
 */

export const FREE_CV_REVIEW_FLAG = "free-cv-review";

export const FREE_CV_REVIEW_MIN_CHARS = 50;
export const FREE_CV_REVIEW_MAX_CHARS = 15_000;
/** Findings shown at most. Enough for a full pass, short enough to act on. */
export const FREE_CV_REVIEW_MAX_FINDINGS = 8;

export const REVIEW_DIMENSIONS = [
	"ats_parse",
	"impact",
	"keywords",
	"seniority",
	"clarity",
] as const;
export type ReviewDimension = (typeof REVIEW_DIMENSIONS)[number];

export type FindingSeverity = "high" | "medium" | "low";

export interface FreeCvReviewFinding {
	dimension: ReviewDimension;
	severity: FindingSeverity;
	/** A line copied from the CV. Verified present by the parser. */
	quote: string;
	/** The same line, rewritten the way a recruiter would want it. */
	rewrite: string;
	why: string;
}

export interface FreeCvReviewDimensionScore {
	key: ReviewDimension;
	/** 0-20. Five dimensions sum to the 0-100 overall score. */
	score: number;
	verdict: string;
}

export interface FreeCvReview {
	overallScore: number;
	dimensions: FreeCvReviewDimensionScore[];
	findings: FreeCvReviewFinding[];
	/** Keywords from live jobs the CV should add (subset of the market input). */
	keywordsToAdd: string[];
	summary: string;
	/** Findings the model returned whose quote was not in the CV (dropped). */
	droppedFindings: number;
}

export interface FreeCvReviewMarketInput {
	roleLabel: string;
	totalActiveJobs: number;
	matchPct: number;
	missingKeywords: string[];
	topKeywords: string[];
}

export function freeCvReviewPrompt(locale: "id" | "en"): string {
	const lang =
		locale === "en"
			? "Write every string value in English."
			: "Tulis setiap nilai string dalam bahasa Indonesia yang jelas dan sopan (pakai 'kamu').";
	return `You are a senior recruiter who has screened thousands of CVs for remote and Indonesian employers. Review the CV the way you would before deciding whether to call the candidate.

You receive:
- CV_TEXT: the candidate's CV as plain text.
- MARKET_DATA: real numbers from our live job database for the candidate's role (role label, active job count, keyword match %, keywords live jobs ask for that the CV lacks). Never invent numbers that are not in MARKET_DATA.

Score five dimensions, each 0-20:
- ats_parse: can an applicant tracking system read it (standard headings, contact details, dates, no tables or columns implied by the text, no graphics-only content).
- impact: achievements with numbers (%, Rp, $, time saved, volume) versus duty lists.
- keywords: coverage of the keywords live jobs for this role ask for (MARKET_DATA).
- seniority: does the CV signal a clear level (scope, ownership, team size, years).
- clarity: scannable in six seconds, short bullets, strong verbs, no filler.

Then list 4-8 findings, most important first. Every finding MUST:
- "quote": copy ONE line from CV_TEXT exactly, character for character (max 200 characters). Never paraphrase it and never quote a line that is not there.
- "rewrite": the same line rewritten as you would want it. Keep the candidate's facts. If a number is missing, insert a clear placeholder such as "[X]%" or "[jumlah]" rather than inventing one.
- "why": one sentence on why it matters to a recruiter or an ATS.
- "dimension": one of ats_parse, impact, keywords, seniority, clarity.
- "severity": high, medium or low.

"keywords_to_add": up to 6 keywords taken ONLY from MARKET_DATA.missing_keywords that this candidate could honestly add.
"summary": two sentences, the recruiter's honest first impression and the single most valuable fix.

Never mention the candidate's name, employer names or personal details. Avoid generic advice ("keep learning", "be confident"). ${lang}

Output a single JSON object and nothing else:
{
  "dimensions": [{"key": "ats_parse", "score": 0, "verdict": "..."}, ...all five],
  "findings": [{"dimension": "impact", "severity": "high", "quote": "...", "rewrite": "...", "why": "..."}],
  "keywords_to_add": ["..."],
  "summary": "..."
}`;
}

/** The user message: CV first, then the market numbers it is judged against. */
export function buildFreeCvReviewUserMessage(
	cvText: string,
	market: FreeCvReviewMarketInput,
): string {
	return `CV_TEXT:\n${cvText}\n\nMARKET_DATA:\n${JSON.stringify(
		{
			role: market.roleLabel,
			total_active_jobs: market.totalActiveJobs,
			match_pct: market.matchPct,
			missing_keywords: market.missingKeywords.slice(0, 10),
			top_keywords: market.topKeywords.slice(0, 20),
		},
		null,
		2,
	)}`;
}

/** Lowercase, collapse whitespace, strip bullets/quotes for quote matching. */
export function normalizeForQuoteMatch(text: string): string {
	return text
		.toLowerCase()
		.replace(/[‘’“”"'`]/g, "")
		.replace(/^[\s\-–—•*·●▪◦]+/gm, "")
		.replace(/\s+/g, " ")
		.trim();
}

/**
 * `true` when `quote` appears in the CV after normalization. A quote shorter
 * than 8 normalized characters never counts: "Skills" is in every CV and
 * tells the seeker nothing about which line to change.
 */
export function quoteIsInCv(quote: string, normalizedCv: string): boolean {
	const q = normalizeForQuoteMatch(quote).replace(/[.;,]+$/, "");
	if (q.length < 8) return false;
	return normalizedCv.includes(q);
}

function str(v: unknown, max: number): string {
	return typeof v === "string" ? v.trim().slice(0, max) : "";
}

function clampScore(v: unknown): number {
	const n = typeof v === "number" ? v : Number.parseFloat(String(v));
	if (!Number.isFinite(n)) return 0;
	return Math.max(0, Math.min(20, Math.round(n)));
}

function extractJsonObject(raw: string): unknown {
	const first = raw.indexOf("{");
	const last = raw.lastIndexOf("}");
	if (first === -1 || last <= first) return null;
	try {
		return JSON.parse(raw.slice(first, last + 1));
	} catch {
		return null;
	}
}

const SEVERITY_RANK: Record<FindingSeverity, number> = {
	high: 0,
	medium: 1,
	low: 2,
};

/**
 * Parses and validates the model's JSON. Returns `null` when the response is
 * unusable: no JSON, fewer than three of the five dimensions, or no finding
 * that survives the quote check (a review with no concrete line to fix is
 * not a review).
 */
export function parseFreeCvReview(
	raw: string,
	cvText: string,
	allowedKeywords: readonly string[],
): FreeCvReview | null {
	const obj = extractJsonObject(raw);
	if (typeof obj !== "object" || obj === null) return null;
	const o = obj as Record<string, unknown>;

	const dimsIn = Array.isArray(o.dimensions) ? o.dimensions : [];
	const byKey = new Map<ReviewDimension, FreeCvReviewDimensionScore>();
	for (const d of dimsIn) {
		if (typeof d !== "object" || d === null) continue;
		const r = d as Record<string, unknown>;
		const key = r.key as ReviewDimension;
		if (!REVIEW_DIMENSIONS.includes(key) || byKey.has(key)) continue;
		byKey.set(key, {
			key,
			score: clampScore(r.score),
			verdict: str(r.verdict, 200),
		});
	}
	if (byKey.size < 3) return null;
	// A dimension the model skipped scores 0 rather than vanishing, so the
	// overall stays on the same 0-100 scale for every review.
	const dimensions = REVIEW_DIMENSIONS.map(
		(key) => byKey.get(key) ?? { key, score: 0, verdict: "" },
	);
	const overallScore = dimensions.reduce((sum, d) => sum + d.score, 0);

	const normalizedCv = normalizeForQuoteMatch(cvText);
	const findingsIn = Array.isArray(o.findings) ? o.findings : [];
	const findings: FreeCvReviewFinding[] = [];
	const seenQuotes = new Set<string>();
	let dropped = 0;
	for (const f of findingsIn) {
		if (typeof f !== "object" || f === null) continue;
		const r = f as Record<string, unknown>;
		const quote = str(r.quote, 200);
		const rewrite = str(r.rewrite, 400);
		if (!quote || !rewrite) {
			dropped++;
			continue;
		}
		if (!quoteIsInCv(quote, normalizedCv)) {
			dropped++;
			continue;
		}
		const normQuote = normalizeForQuoteMatch(quote);
		// A rewrite identical to its quote (bar trailing punctuation) is not a fix.
		const bare = (t: string) =>
			normalizeForQuoteMatch(t).replace(/[.;,!]+$/, "");
		if (bare(rewrite) === bare(quote)) {
			dropped++;
			continue;
		}
		if (seenQuotes.has(normQuote)) continue;
		seenQuotes.add(normQuote);
		const dimension = REVIEW_DIMENSIONS.includes(r.dimension as ReviewDimension)
			? (r.dimension as ReviewDimension)
			: "clarity";
		const severity: FindingSeverity =
			r.severity === "high" || r.severity === "low" ? r.severity : "medium";
		findings.push({
			dimension,
			severity,
			quote,
			rewrite,
			why: str(r.why, 240),
		});
	}
	if (findings.length === 0) return null;
	findings.sort(
		(a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity],
	);

	const allowed = new Set(allowedKeywords.map((k) => k.toLowerCase().trim()));
	const keywordsToAdd = (
		Array.isArray(o.keywords_to_add) ? o.keywords_to_add : []
	)
		.map((k) => str(k, 60).toLowerCase())
		.filter((k) => k && allowed.has(k))
		.filter((k, i, arr) => arr.indexOf(k) === i)
		.slice(0, 6);

	return {
		overallScore,
		dimensions,
		findings: findings.slice(0, FREE_CV_REVIEW_MAX_FINDINGS),
		keywordsToAdd,
		summary: str(o.summary, 500),
		droppedFindings: dropped,
	};
}
